import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchBox } from '../../../features/search/components/SearchBox';

interface FloatingSearchBoxProps {
  className?: string;
}

export const FloatingSearchBox: React.FC<FloatingSearchBoxProps> = ({
  className = '',
}) => {
  const [searchType, setSearchType] = useState<'safari' | 'trip'>('safari');
  const navigate = useNavigate();

  // Dynamic shadow colors based on search type
  const shadowColor = searchType === 'safari' 
    ? 'from-primary-500/20 via-accent-coral-500/20 to-accent-teal-500/20' 
    : 'from-accent-teal-500/20 via-primary-500/20 to-accent-coral-500/20';

  const shadowColorSecondary = searchType === 'safari'
    ? 'from-primary-500/10 via-accent-coral-500/10 to-accent-teal-500/10'
    : 'from-accent-teal-500/10 via-primary-500/10 to-accent-coral-500/10';

  return (
    <div className={`relative z-20 -mt-32 sm:-mt-20 lg:-mt-24 mb-8 sm:mb-12 lg:mb-16 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          {/* Enhanced glass effect with backdrop blur */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl"></div>
          
          {/* Dynamic animated glow effect based on search type */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${shadowColor} rounded-3xl blur-xl opacity-60 animate-pulse`}></div>
          
          {/* Additional depth layer with dynamic colors */}
          <div className={`absolute -inset-2 bg-gradient-to-r ${shadowColorSecondary} rounded-3xl blur-2xl opacity-40`}></div>
          
          {/* Mobile-specific adjustments */}
          <div className="relative z-10 transform transition-all duration-300 hover:scale-[1.02]">
            <SearchBox
              variant="glass"
              buttonText={searchType === 'safari' ? 'Find Safaris' : 'Find Trips'}
              colorTheme={searchType === 'safari' ? 'primary' : 'teal'}
              title={
                <div className="text-center mb-4">
                  <h3 className="text-2xl sm:text-3xl font-black text-text-primary mb-2 drop-shadow-lg tracking-tight">
                    Start Your Adventure
                  </h3>
                  <p className="text-text-secondary text-sm sm:text-base drop-shadow-md font-medium">
                    Discover {searchType === 'safari' ? 'incredible safari experiences' : 'reliable transportation'} across Tanzania
                  </p>
                </div>
              }
              toggle={
                <div className="flex items-center justify-center gap-2 sm:gap-3 mt-4 pt-3">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <span className={`text-sm font-semibold transition-colors duration-300 ${searchType === 'safari' ? 'text-text-primary' : 'text-text-tertiary'}`}>
                    Safari
                  </span>
                  <div className="relative w-16 h-8 sm:w-18 sm:h-8 bg-gray-200 rounded-full p-1 transition-all duration-300 hover:bg-gray-300">
                    {/* Sliding handle */}
                    <div
                      className={`absolute top-1 left-1 w-6 h-6 sm:w-6 sm:h-6 bg-white rounded-full shadow-lg transition-all duration-300 ease-out transform ${
                        searchType === 'safari' ? 'translate-x-0' : 'translate-x-8 sm:translate-x-10'
                      }`}
                    />
                    
                    {/* Background indicators */}
                    <div className="flex items-center justify-between w-full h-full px-1.5 sm:px-2">
                      <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                        searchType === 'safari' ? 'bg-primary-500 shadow-sm' : 'bg-gray-400'
                      }`} />
                      <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                        searchType === 'trip' ? 'bg-accent-teal-500 shadow-sm' : 'bg-gray-400'
                      }`} />
                    </div>
                    
                    {/* Click areas */}
                    <button
                      onClick={() => setSearchType('safari')}
                      className="absolute inset-0 w-1/2 h-full focus:outline-none"
                      aria-label="Switch to safari search"
                    />
                    <button
                      onClick={() => setSearchType('trip')}
                      className="absolute inset-0 left-1/2 w-1/2 h-full focus:outline-none"
                      aria-label="Switch to trip search"
                    />
                  </div>
                  <span className={`text-sm font-semibold transition-colors duration-300 ${searchType === 'trip' ? 'text-text-primary' : 'text-text-tertiary'}`}>
                    Trips
                  </span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>
              }
              onSearch={(params) => {
                // Navigate to appropriate discovery page based on search type
                const searchParams = new URLSearchParams({
                  location: params.location,
                  start_date: params.startDate.toISOString().split('T')[0],
                  guests: params.guests.toString(),
                });
                
                if (params.endDate && params.endDate !== params.startDate) {
                  searchParams.append('end_date', params.endDate.toISOString().split('T')[0]);
                }
                
                const targetRoute = searchType === 'safari' ? '/safaris-search' : '/transport-search';
                navigate(`${targetRoute}?${searchParams.toString()}`);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}; 